(ns manatee.components.editors.component
  (:require [manatee.components.editors.toolbar :as toolbar]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [rothko.editor.component :as component]
            [rothko.editor.manatee.actions]))

(defn only-speech-unit-tags
  [token]
  (or (contains? #{:foreign-proper-noun :speech-unit-question :speech-unit-incomplete :speech-unit-statement :unknown :code-switch}
                 (:name token))
      (not= (:type token) :tag)))

(defn speech-corrected-token?
  [token]
  (and (not (some #{:disfluency :partial :backchannel} (map :name (:marks token))))
       (only-speech-unit-tags token)
       (not (:system? token))
       (not (:tagged? token))
       (not (:edited? token))))

(defn script-annotation-corrected-token?
  [{:keys [tagged? edited?]}]
  (not (or tagged? edited?)))

(defn speech-script-step
  [view-token? next-token]
  (cond
    view-token? 1
    (= :space (:type next-token)) 2 ; Skip next token (space) when stripping out tokens for corrected view.
    :else 1))

(defn skip-original-token?
  "Should TOKEN be skipped when translating between document offsets in an
   original script and the selection in the corresponding annotated script?"
  [{:keys [type system?]
    :as _token}]
  (and (#{:edit :space-edit :tag} type)
       (not system?)))

(defn watch-for-view-change
  [!state on-change-view]
  (when on-change-view
    (add-watch !state :on-change-view (fn [_ _ old new]
                                        (when (not= (:view old)
                                                    (:view new))
                                          (on-change-view (:view new)))))))

(defn jump-to-selected-token
  "Jump the wave-form to the selected token in the script."
  [{:keys                 [script]
    {:keys [start-index]} :selection
    :as                   _document}]
  (letfn [(is-1st-preference-token? [token]
            (and (contains? token :start)
                 (not (:edited? token))))
          (is-2nd-preference-token? [token]
            (contains? token :start))]

    ;; Working backwards from the selected token, find the first token that
    ;; satisfies the 1st preference predicate.
    (if-let [{:keys [start]
              :as   _token} (first (for [idx   (range start-index -1 -1)
                                         :let  [token (nth script idx)]
                                         :when (is-1st-preference-token? token)]
                                     token))]
      (rf/dispatch [:wave-player/set-time start])

      ;; If none found, find the first token that satisfies the 2nd preference
      ;; predicate.
      (when-let [{:keys [start]
                  :as   _token} (first (for [idx   (range start-index -1 -1)
                                             :let  [token (nth script idx)]
                                             :when (is-2nd-preference-token? token)]
                                         token))]
        (rf/dispatch [:wave-player/set-time start])))))

(rf/reg-event-fx ::update-playhead (fn playhead-update [_ [_ playhead]] {:playhead playhead}))

(defn component-fn
  [{:keys [::implementation ::corrected-token? ::script-step ::on-context-menu ::make-toolbar-items ::make-ro-toolbar-items
           ::skip-corrected-token?-fn ::skip-original-token?-fn ::skip-succeeding-space?]}]
  (fn component
    [{:keys [init document on-blur on-mount on-transform on-change-view initial-view read-only? hide-toolbar?
             custom-markers immutable-text? spelling-dictionary editor-event-opts]}]
    (let [appearance #{implementation}
          !state (r/atom {:render false
                          :view (or initial-view :annotated)
                          :ro-view-hidden true
                          :ro-view :ro-original
                          :implementation implementation
                          :focus-at 0})
          !document (atom document)
          toolbar (when-not hide-toolbar? [toolbar/toolbar
                                           {:!state !state :appearance appearance}
                                           (make-toolbar-items editor-event-opts custom-markers)])
          ro-view-toolbar (when (and (not hide-toolbar?)
                                     make-ro-toolbar-items)
                            [toolbar/toolbar
                             {:!state !state
                              :appearance appearance}
                             (make-ro-toolbar-items)
                             []])]
      (watch-for-view-change !state on-change-view)
      (rf/reg-fx :playhead (fn [playhead] (swap! !state assoc :focus-at playhead)))
      [component/dom-editor
       {:!document !document
        :!state !state
        :editflag-fn #(get {:can-interact? true
                            :can-type? (and (not read-only?)
                                            (not immutable-text?)
                                            (= :annotated (:view @!state)))
                            :click-to-anno? true
                            :type-to-anno? (and (not read-only?)
                                                (not= :annotated (:view @!state)))} %)
        :appearance appearance
        :corrected-view-filter corrected-token?
        :script-step script-step
        :init init
        :on-blur on-blur
        :on-transform on-transform
        :on-mount on-mount
        :on-context-menu on-context-menu
        :toolbar toolbar
        :ro-view-toolbar ro-view-toolbar
        :skip-corrected-token?-fn skip-corrected-token?-fn
        :skip-original-token?-fn skip-original-token?-fn
        :skip-succeeding-space? skip-succeeding-space?
        :spelling-dictionary spelling-dictionary}])))

(def transcript-annotation-component
  (component-fn
   {::implementation :est-transcription-annotator
    ::corrected-token? speech-corrected-token?
    ::script-step speech-script-step
    ::on-context-menu jump-to-selected-token
    ::make-toolbar-items toolbar/make-transcript-annotation-toolbar
    ::make-ro-toolbar-items (constantly toolbar/annotation-ro-view-toolbar)
    ::skip-corrected-token?-fn (complement speech-corrected-token?)
    ::skip-original-token?-fn skip-original-token?
    ::skip-succeeding-space? true}))

(def error-annotation-component
  (component-fn
   {::implementation :est-error-annotator
    ::corrected-token? speech-corrected-token?
    ::script-step speech-script-step
    ::make-toolbar-items toolbar/error-annotation-toolbar
    ::make-ro-toolbar-items (constantly toolbar/annotation-ro-view-toolbar)
    ::skip-corrected-token?-fn (complement speech-corrected-token?)
    ::skip-original-token?-fn skip-original-token?
    ::skip-succeeding-space? true}))

(def script-annotation-component
  (component-fn
   {::implementation :script-annotator
    ::corrected-token? script-annotation-corrected-token?
    ::script-step (constantly 1)
    ::make-toolbar-items toolbar/make-script-annotation-toolbar
    #_#_::make-ro-toolbar-items (constantly toolbar/annotation-ro-view-toolbar)
    ::skip-corrected-token?-fn (complement script-annotation-corrected-token?)
    ::skip-original-token?-fn skip-original-token?
    ::skip-succeeding-space? false}))

(def mwe-annotation-component
  (component-fn
   {::implementation :mwe-annotator
    ::corrected-token? (constantly true)
    ::script-step (constantly 1)
    ::make-toolbar-items toolbar/mwe-annotation-toolbar
    ::skip-succeeding-space? false}))
